import { Fragment } from 'preact'
import { useState } from 'preact/hooks'

import type {
  CookieCategory,
  CookieRecord,
} from '../../../services/cookieTranslationService/types'

import type { CookieConsentTranslator } from '#src/assets/lib/i18n.ts'
import { CookieConsentBanner } from '#src/assets/ui/banner/CookieConsentBanner.tsx'
import type { CookieConsentEngine } from '#src/assets/ui/mount/cookieConsentEngine.ts'
import type { CookieConsentUiVisibility } from '#src/assets/ui/mount/types.ts'
import { PreferencesModal } from '#src/assets/ui/preferences/PreferencesModal.tsx'
import { SettingsDialog } from '#src/assets/ui/settings/SettingsDialog.tsx'
import { FloatingActionButton } from '#src/assets/ui/widget/FloatingActionButton.tsx'
import type { SiteConfig } from '#src/models/site-configs/siteConfigs.ts'

export function CookieConsentWidget({
  t,
  locale,
  siteConfig,
  cookiesByCategory,
  engine,
  visibility,
  registerRerender,
  onOpenSettingsDialog,
  onHideSettingsDialog,
  onShowPreferencesModal,
  onHidePreferencesModal,
  onConsentSaved,
}: {
  t: CookieConsentTranslator
  locale: string
  siteConfig: SiteConfig
  cookiesByCategory: Record<CookieCategory, Array<CookieRecord>>
  engine: CookieConsentEngine
  visibility: CookieConsentUiVisibility
  registerRerender: (rerender: () => void) => void
  onOpenSettingsDialog: () => void
  onHideSettingsDialog: () => void
  onShowPreferencesModal: () => void
  onHidePreferencesModal: () => void
  onConsentSaved: () => void
}) {
  const [, setRenderVersion] = useState(0)

  // Register synchronously so controller calls made right after mount can render
  // the initial banner before effects have a chance to run.
  registerRerender(() => {
    setRenderVersion(version => version + 1)
  })

  const handleAcceptAll = (): void => {
    engine.acceptAll()
    onConsentSaved()
  }

  const handleRejectAll = (): void => {
    engine.rejectAll()
    onConsentSaved()
  }

  return (
    <Fragment>
      <FloatingActionButton
        visible={visibility.actionButtonVisible}
        onClick={onOpenSettingsDialog}
      />
      <CookieConsentBanner
        t={t}
        locale={locale}
        siteConfig={siteConfig}
        cookiesByCategory={cookiesByCategory}
        visible={visibility.bannerVisible}
        onAcceptAll={handleAcceptAll}
        onRejectAll={handleRejectAll}
        onManagePreferences={onShowPreferencesModal}
      />
      <SettingsDialog
        t={t}
        locale={locale}
        siteConfig={siteConfig}
        cookiesByCategory={cookiesByCategory}
        visible={visibility.settingsDialogVisible}
        onClose={onHideSettingsDialog}
        onAdjustPreferences={onShowPreferencesModal}
      />
      <PreferencesModal
        t={t}
        siteConfig={siteConfig}
        cookiesByCategory={cookiesByCategory}
        engine={engine}
        visible={visibility.preferencesModalVisible}
        onClose={onHidePreferencesModal}
        onConsentSaved={onConsentSaved}
      />
    </Fragment>
  )
}
